import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { OpeningHoursPicker, summarizeHours } from "@/components/OpeningHoursPicker";
import { toast } from "sonner";
import { useCapAutofill, CapChoices, cleanCap } from "@/components/CapAutofill";
import { Store, MapPin, Share2, Building2, Clock, ExternalLink, RefreshCw } from "lucide-react";

const EMPTY = { opening_hours: "", opening_schedule: null, on_street: false, address: "", city: "", zip: "", province: "", country: "IT", lat: null, lng: null, website: "", facebook: "", instagram: "", whatsapp: "", services: "", show_on_map: false };
const ADDR = ["address", "zip", "city", "province", "country"];
const hasPos = (p) => p.lat != null && p.lat !== "" && p.lng != null && p.lng !== "";

function Position({ p, geo, onLocate }) {
  return (
    <div className="rounded-lg border border-border p-3 flex flex-wrap items-center gap-3 text-sm" data-testid="profile-position">
      <MapPin className="w-4 h-4 text-primary shrink-0" />
      <div className="flex-1 min-w-[200px]">
        {geo.busy ? <span className="text-muted-foreground" data-testid="profile-locating">Cerco la posizione sulla mappa…</span>
          : hasPos(p) ? <><span className="font-medium" data-testid="profile-coords">Lat {Number(p.lat).toFixed(5)} · Long {Number(p.lng).toFixed(5)}</span>{geo.label && <span className="text-xs text-muted-foreground block">{geo.label}</span>}</>
            : <span className="text-muted-foreground" data-testid="profile-coords-missing">{geo.error || "La posizione si calcola da sola dall'indirizzo."}</span>}
      </div>
      {hasPos(p) && <a className="text-xs text-primary inline-flex items-center gap-1 underline" href={`https://www.openstreetmap.org/?mlat=${p.lat}&mlon=${p.lng}#map=17/${p.lat}/${p.lng}`} target="_blank" rel="noreferrer" data-testid="profile-map-link">Vedi sulla mappa <ExternalLink className="w-3 h-3" /></a>}
      <Button type="button" size="sm" variant="outline" className="gap-1" onClick={onLocate} disabled={geo.busy} data-testid="profile-locate"><RefreshCw className="w-3.5 h-3.5" /> Ricalcola</Button>
    </div>
  );
}

const Section = ({ icon: Icon, title, children, testid }) => (
  <Card className="p-5 glow-border space-y-3" data-testid={testid}>
    <p className="font-head font-semibold flex items-center gap-2"><Icon className="w-4 h-4 text-primary" /> {title}</p>
    {children}
  </Card>
);

const Field = ({ k, label, p, set, onBlur, ...rest }) => (
  <div><Label>{label}</Label><Input className="mt-1.5" data-testid={`profile-${k}`} value={p[k] || ""} onChange={(e) => set(k, e.target.value)} onBlur={onBlur} {...rest} /></div>
);

export default function DealerProfile() {
  const [p, setP] = useState(EMPTY);
  const [legal, setLegal] = useState(null);
  const [prefilled, setPrefilled] = useState([]);
  const [geo, setGeo] = useState({ busy: false, label: "", error: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v, ...(ADDR.includes(k) ? { lat: null, lng: null } : {}) }));
  const cap = useCapAutofill((pl) => { set("city", pl.city || ""); set("province", pl.province || ""); });
  const setZip = (k, v) => { const italian = !p.country || p.country === "IT"; const z = italian ? cleanCap(v) : v; set(k, z); if (italian) cap.lookup(z); };

  const locate = async (src) => {
    if (!src.address?.trim()) { setGeo({ busy: false, label: "", error: "Inserisci l'indirizzo per calcolare la posizione." }); return; }
    setGeo({ busy: true, label: "", error: "" });
    try {
      const { data } = await api.post("/account/dealer-profile/geocode", { address: src.address, zip: src.zip, city: src.city, province: src.province, country: src.country });
      setP((x) => ({ ...x, lat: data.lat, lng: data.lng })); setGeo({ busy: false, label: data.label, error: "" });
    } catch (e) { setGeo({ busy: false, label: "", error: formatApiErrorDetail(e.response?.data?.detail) || "Posizione non trovata" }); }
  };

  useEffect(() => {
    api.get("/account/dealer-profile").then((r) => {
      const { legal: l, prefilled_from_legal: pf, ...rest } = r.data || {};
      const next = { ...EMPTY, ...rest };
      setP(next); setLegal(l || null); setPrefilled(pf || []);
      if (next.address && (!hasPos(next) || (pf || []).length)) locate(next);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const useLegal = () => { const next = { ...p, ...legal, lat: null, lng: null }; setP(next); setPrefilled(ADDR.filter((k) => legal[k])); locate(next); };
  const blur = () => { if (!hasPos(p)) locate(p); };

  const save = async () => {
    const sch = p.opening_schedule;
    if (sch && Object.values(sch).some((d) => d?.open && d.slots.some(([a, b]) => !a || !b || a >= b))) { toast.error("Controlla gli orari: l'apertura deve essere prima della chiusura."); return; }
    setSaving(true);
    try {
      const num = (v) => (v === "" || v == null ? null : Number(v));
      const payload = { ...p, opening_hours: p.opening_schedule ? summarizeHours(p.opening_schedule) : p.opening_hours, lat: num(p.lat), lng: num(p.lng) };
      await api.put("/account/dealer-profile", payload);
      toast.success("Vetrina aggiornata"); setPrefilled([]);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const legalDiffers = legal?.address && ADDR.some((k) => legal[k] && legal[k] !== p[k]);
  return (
    <div className="space-y-4 max-w-3xl" data-testid="dealer-profile-page">
      <div>
        <div className="flex items-center gap-2"><Store className="w-4 h-4 text-primary" /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Gestione</span></div>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-2">La mia vetrina</h1>
        <p className="text-sm text-muted-foreground mt-1">Le informazioni pubbliche del tuo negozio, visibili sulla mappa dei dealer.</p>
      </div>

      <Section icon={MapPin} title="Dove si trova il negozio" testid="profile-address-section">
        {prefilled.length > 0 && <p className="text-xs rounded-md bg-primary/10 border border-primary/30 px-3 py-2 flex items-center gap-2" data-testid="profile-prefilled-note"><Building2 className="w-3.5 h-3.5 text-primary shrink-0" /> Abbiamo preso l'indirizzo dalla sede legale. Se il negozio è altrove, modificalo qui sotto.</p>}
        {!prefilled.length && legalDiffers && <button type="button" className="text-xs text-primary inline-flex items-center gap-1 hover:underline" onClick={useLegal} data-testid="profile-use-legal"><Building2 className="w-3.5 h-3.5" /> Usa l'indirizzo della sede legale</button>}
        <Field k="address" label="Indirizzo" p={p} set={set} onBlur={blur} placeholder="Via Roma 1" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field k="zip" label="CAP" p={p} set={setZip} onBlur={blur} placeholder="Inizia dal CAP" inputMode="numeric" />
          <Field k="city" label="Città" p={p} set={set} onBlur={blur} placeholder="Milano" />
          <Field k="province" label="Provincia" p={p} set={(k, v) => set(k, v.toUpperCase())} onBlur={blur} placeholder="MI" maxLength={2} />
          <Field k="country" label="Paese" p={p} set={(k, v) => set(k, v.toUpperCase())} onBlur={blur} placeholder="IT" maxLength={2} />
        </div>
        <CapChoices cap={cap} testid="profile-cap" />
        <Position p={p} geo={geo} onLocate={() => locate(p)} />
        <p className="text-xs text-muted-foreground">Questo indirizzo è anche il <strong>mittente</strong> delle spedizioni «Parti dal mio negozio».</p>
      </Section>

      <Section icon={Clock} title="Orari di apertura" testid="profile-hours-section">
        {!p.opening_schedule && p.opening_hours && <p className="text-xs text-muted-foreground" data-testid="profile-hours-legacy">Orari scritti finora: <strong className="text-foreground">{p.opening_hours}</strong></p>}
        <OpeningHoursPicker value={p.opening_schedule} onChange={(v) => setP((x) => ({ ...x, opening_schedule: v }))} />
      </Section>

      <Section icon={Store} title="Presentazione (facoltativa)" testid="profile-about-section">
        <Textarea data-testid="profile-services" value={p.services || ""} onChange={(e) => set("services", e.target.value)} placeholder="Racconta il tuo negozio e i servizi che offri: attivazione SIM, ricariche, eSIM, bollettini…" />
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="flex items-center justify-between rounded-lg border border-border p-3 gap-3"><div><Label>Mostra sulla mappa</Label><p className="text-xs text-muted-foreground mt-0.5">Appari nella mappa pubblica dei dealer.</p></div><Switch checked={!!p.show_on_map} onCheckedChange={(v) => set("show_on_map", v)} data-testid="profile-showonmap-switch" /></div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3 gap-3"><div><Label>Negozio su strada</Label><p className="text-xs text-muted-foreground mt-0.5">Punto vendita aperto ai clienti.</p></div><Switch checked={!!p.on_street} onCheckedChange={(v) => set("on_street", v)} data-testid="profile-onstreet-switch" /></div>
        </div>
      </Section>

      <Section icon={Share2} title="Social & contatti" testid="profile-social-section">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field k="website" label="Sito web" p={p} set={set} placeholder="https://…" />
          <Field k="whatsapp" label="WhatsApp" p={p} set={set} type="tel" placeholder="+39 3xx…" />
          <Field k="facebook" label="Facebook" p={p} set={set} placeholder="https://facebook.com/…" />
          <Field k="instagram" label="Instagram" p={p} set={set} placeholder="https://instagram.com/…" />
        </div>
      </Section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="profile-save-button">{saving ? "Salvataggio…" : "Salva vetrina"}</Button>
      </div>
    </div>
  );
}
